import i18n from 'i18next';
import type { GrantType, GrantStatus, GrantRequest } from '@/types/api';
import type { ChipTone } from '@/components/editorial/StatusChip';
import { RELATIVE_DAYS_LIMIT } from '@/lib/application-timing';
import { trfEventDate, trfMonthDay, viewerCalendarDaysSince } from '@/lib/trfTime';
import { englishLabels, labelFor } from '@/i18n/labels';
import type { AnyTFunction } from '@/i18n/msg';
import { formatMoneyCents } from '@/i18n/intl';

// Amounts are integer USD cents end-to-end (DECISIONS 2026-05-31). All
// cents<->dollars conversion lives here so producer/consumer never disagree.

export function formatCents(cents: number | null | undefined): string {
  if (cents == null) return '—';
  // Two decimals, USD, in the reader's locale (#2109 R3-B6); en is unchanged.
  return formatMoneyCents(cents);
}

/** Parse a user-entered dollar string into integer cents, or null if invalid. */
export function dollarsToCents(input: string): number | null {
  const n = Number(String(input).replace(/[$,\s]/g, ''));
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

export const GRANT_TYPES: GrantType[] = ['signature', 'travel', 'family_fun', 'path_to_heaven'];
export const GRANT_STATUSES: GrantStatus[] = [
  'draft', 'submitted', 'under_review', 'approved', 'denied', 'fulfilled', 'withdrawn',
];

/**
 * Grant type and status names are KEY maps, resolved at render with
 * `grantTypeLabel` / `grantStatusLabel` (#2109 R3-B4). The type names follow
 * the glossary ruling (Integration); `grants:type.path_to_heaven` names a
 * bereavement program; it stays English in every language (glossary), a
 * wording Victor reviewed on 2026-09-30, so it never ships as a literal
 * machine rendering.
 */
export const GRANT_TYPE_LABEL_KEY = {
  signature: 'grants:type.signature',
  travel: 'grants:type.travel',
  family_fun: 'grants:type.family_fun',
  path_to_heaven: 'grants:type.path_to_heaven',
} as const satisfies Record<GrantType, string>;

/** R4 compatibility getter: staff files keep reading English (deleted in R4). */
export const GRANT_TYPE_LABEL: Readonly<Record<GrantType, string>> = englishLabels(GRANT_TYPE_LABEL_KEY);

/** The grant type in the reader's language; `t` defaults to the active language. */
export function grantTypeLabel(type: GrantType | null | undefined, t: AnyTFunction = i18n.t): string {
  return labelFor(GRANT_TYPE_LABEL_KEY, type, t);
}

/**
 * One label per status, on every surface — staff and parent (2026-09-17, the
 * staff-lists wave). Until then the roster's chip said Applied · Paid while
 * this map said Submitted · Fulfilled for the same two keys, so one status had
 * two names on two screens. The family's words won: a family APPLIES for a
 * grant and the grant is PAID; "submitted" and "fulfilled" were the state
 * machine's words. The keys never change — only what prints.
 * `(?)` grant-status-vocabulary marks the choice on Requests.
 *
 * 2026-09-19 (grant awards wave, C-2): `fulfilled` prints **Closed**. An
 * award is paid over time — a Signature grant in twelve monthly payments — so
 * "Paid" named one moment of a record that is now closed by staff ("Close
 * award", with a reason) or automatically when its payments reach the award.
 *
 * 2026-10-25 (DECISIONS; TRF answers 2026-10-24 Q8, verbatim): `submitted` and
 * `under_review` both print **Pending**, for staff and families alike. A
 * request waits for the TRF team's decision, and TRF names no review stage
 * the family should see. The two keys stay, as every key does.
 */
export const GRANT_STATUS_LABEL_KEY = {
  draft: 'grants:status.draft',
  submitted: 'grants:status.submitted',
  under_review: 'grants:status.under_review',
  approved: 'grants:status.approved',
  denied: 'grants:status.denied',
  fulfilled: 'grants:status.fulfilled',
  withdrawn: 'grants:status.withdrawn',
} as const satisfies Record<GrantStatus, string>;

/** R4 compatibility getter: staff files keep reading English (deleted in R4). */
export const GRANT_STATUS_LABEL: Readonly<Record<GrantStatus, string>> = englishLabels(GRANT_STATUS_LABEL_KEY);

/** The grant status in the reader's language; `t` defaults to the active language. */
export function grantStatusLabel(status: GrantStatus | null | undefined, t: AnyTFunction = i18n.t): string {
  return labelFor(GRANT_STATUS_LABEL_KEY, status, t);
}

/**
 * #2821 (Victor, 2026-10-13): what a FAMILY reads for a grant's status. Since
 * #2800 TRF creates the Signature grant itself, at admission or at the
 * committee's approval of a renewal, so the family never applied for it and
 * "Applied" was untrue. Until the grant closes, every stage of it (applied,
 * under review, approved) reads **Awarded** on the family's surfaces. Denied,
 * Withdrawn and Closed keep their words, and so does every other grant type.
 *
 * Staff surfaces keep `grantStatusLabel` and the state machine's stages: the
 * grants lane still reviews and approves the Signature grant. The family's
 * word lives under its own key (`grants:status.awarded`), so the two
 * vocabularies cannot drift into each other.
 *
 * 2026-10-25 (DECISIONS): every other grant reads **Pending** while it waits
 * for the team, whether it is `submitted` or `under_review`. The family has one
 * word for one wait, so `under_review` reads as `submitted` here: one
 * `grants:status.submitted` label, and one "Pending" chip in the filter
 * rather than two.
 */
export type FamilyGrantStatus = Exclude<GrantStatus, 'under_review'> | 'awarded';

/** Every status a family can read, `awarded` first: the filter's chips. `under_review` reads as `submitted`. */
export const FAMILY_GRANT_STATUSES: readonly FamilyGrantStatus[] = [
  'awarded',
  ...GRANT_STATUSES.filter((s): s is Exclude<GrantStatus, 'under_review'> => s !== 'under_review'),
];

const AWARDED_STAGES: ReadonlySet<GrantStatus> = new Set<GrantStatus>(['submitted', 'under_review', 'approved']);

export function familyGrantStatus(grant: Pick<GrantRequest, 'type' | 'status'>): FamilyGrantStatus {
  if (grant.type === 'signature' && AWARDED_STAGES.has(grant.status)) return 'awarded';
  return grant.status === 'under_review' ? 'submitted' : grant.status;
}

const { under_review: _underReview, ...FAMILY_STAGE_LABEL_KEY } = GRANT_STATUS_LABEL_KEY;
const FAMILY_GRANT_STATUS_LABEL_KEY = {
  ...FAMILY_STAGE_LABEL_KEY,
  awarded: 'grants:status.awarded',
} as const satisfies Record<FamilyGrantStatus, string>;

/** A family status's word (`awarded`, or a stored status): the filter's chips. */
export function familyGrantStatusScopeLabel(status: FamilyGrantStatus, t: AnyTFunction = i18n.t): string {
  return labelFor(FAMILY_GRANT_STATUS_LABEL_KEY, status, t);
}

/** The family's word for a grant's status, in the reader's language. */
export function familyGrantStatusLabel(grant: Pick<GrantRequest, 'type' | 'status'>, t: AnyTFunction = i18n.t): string {
  return familyGrantStatusScopeLabel(familyGrantStatus(grant), t);
}

/** The grant statuses that still wait for staff to Decide the grant. */
const AWAITING_DECIDE: ReadonlySet<GrantStatus> = new Set<GrantStatus>(['submitted', 'under_review']);

/**
 * #2745: the id of the Signature grant an approved renewal created, while
 * that grant still waits for staff to Decide it — or null.
 *
 * The committee's approval creates the grant (#1490) and the renewal card
 * steps aside (`handed_off`), but until staff Decide it the grant has no
 * amount and no schedule, so the row named nothing about the renewal. That
 * one row says "Your renewal was approved. TRF is setting up your grant."
 *
 * How the row is known to be the renewal's: the family wire withholds the
 * grant's `source_application_id` (familyFacingProjection.js, #477), but the
 * renewal read says the renewal is `committee_approved` and
 * `has_grant_request`. That grant is the family's NEWEST Signature grant
 * (a renewal is offered only beneath an earlier Signature grant, and the
 * approval mints exactly one), so it is the newest Signature row, and only
 * while that row is undecided. An initial family's grant (no renewal on
 * file) and every decided grant answer null and read as before.
 */
export function renewalGrantInSetup(
  grants: ReadonlyArray<Pick<GrantRequest, '_id' | 'type' | 'status' | 'created_at'>>,
  renewal: { status?: string | null; has_grant_request?: boolean } | null | undefined,
): string | null {
  if (renewal?.status !== 'committee_approved' || renewal.has_grant_request !== true) return null;
  return signatureGrantInSetup(grants);
}

/**
 * Wave launch-w1014 L5-a: the id of the family's NEWEST Signature grant while
 * it still waits for staff to Decide it (submitted or under review), or null.
 *
 * Admission creates the Signature grant `submitted` (admissionService.js,
 * grantMaterialization.js), and so does every renewal that comes in through
 * `/apply`. Until Decide it has no amount, no schedule and no receipts
 * control, so the row read only "Signature · Awarded" with nothing under it.
 * `renewalGrantInSetup` named the grant only beside a `/renewals` record,
 * which an admission-created grant never has. This one has no renewal
 * precondition (D6): the row says TRF is setting the grant up and that the
 * amount and schedule will appear there. It names no amount (D6).
 *
 * Only the newest Signature row, and only while it is undecided: an older
 * undecided Signature row beneath a newer one is not "being set up", and every
 * other grant type is a request the family filed, so it keeps its own words.
 */
export function signatureGrantInSetup(
  grants: ReadonlyArray<Pick<GrantRequest, '_id' | 'type' | 'status' | 'created_at'>>,
): string | null {
  let newest: Pick<GrantRequest, '_id' | 'type' | 'status' | 'created_at'> | null = null;
  for (const g of grants) {
    if (g.type !== 'signature') continue;
    if (!newest || String(g.created_at ?? '') > String(newest.created_at ?? '')) newest = g;
  }
  return newest && AWAITING_DECIDE.has(newest.status) ? newest._id : null;
}

/** The family's chip tone: an awarded grant is good news, so `ok`. */
export function familyGrantStatusTone(grant: Pick<GrantRequest, 'type' | 'status'>): ChipTone {
  const status = familyGrantStatus(grant);
  return status === 'awarded' ? 'ok' : GRANT_STATUS_TONE[status];
}

/** StatusChip tone per grant status (#762: tone names, not class strings).
 *  `fulfilled` was the indigo-wash chip the "a chip is never indigo" rule
 *  forbids — DECISIONS 2026-08-13 settles it as neutral: the grant is spent
 *  and inert, so it takes the quiet tone, not ok-green. */
export const GRANT_STATUS_TONE: Record<GrantStatus, ChipTone> = {
  draft:        'neutral',
  submitted:    'warn',
  under_review: 'warn',
  approved:     'ok',
  denied:       'danger',
  fulfilled:    'neutral',
  withdrawn:    'neutral',
};

// ── Time in a status (guideline §7, the application-timing shape) ──────────

/**
 * Statuses a grant request cannot leave. A settled row names the day it got
 * there ("Approved Sep 12, 2026") rather than keeping a running count.
 */
export const GRANT_SETTLED_STATUSES: ReadonlySet<GrantStatus> = new Set<GrantStatus>([
  'approved', 'denied', 'fulfilled', 'withdrawn',
]);

type TimedGrant = Pick<
  GrantRequest,
  'status' | 'created_at' | 'submitted_at' | 'reviewed_at' | 'fulfilled_at' | 'audit_trail'
>;

/** The newest audit entry whose action is the given status, or null. */
function auditEnteredAt(grant: TimedGrant, action: string): string | null {
  const entries = (grant.audit_trail ?? []).filter((e) => e.action === action && e.at);
  if (entries.length === 0) return null;
  return entries[entries.length - 1].at;
}

/**
 * When the request entered its CURRENT status — a real clock per status, every
 * one stamped by `GrantRequest.transition` (or by `create` for a draft):
 *
 *   draft        `created_at`
 *   submitted    `submitted_at`
 *   under_review the transition's own audit entry (no dedicated stamp exists)
 *   approved     `reviewed_at`
 *   denied       `reviewed_at`
 *   fulfilled    `fulfilled_at`
 *   withdrawn    the transition's audit entry, else `reviewed_at` (a closed draft)
 *
 * `updated_at` is never read (§1.7): it moves on every edit, so it would
 * measure the last keystroke, not the stage. Null means "we cannot tell".
 */
export function grantStageEnteredAt(grant: TimedGrant): string | null {
  switch (grant.status) {
    case 'draft': return grant.created_at ?? null;
    case 'submitted': return grant.submitted_at ?? auditEnteredAt(grant, 'submitted');
    case 'under_review': return auditEnteredAt(grant, 'under_review');
    case 'approved':
    case 'denied': return grant.reviewed_at ?? auditEnteredAt(grant, grant.status);
    case 'fulfilled': return grant.fulfilled_at ?? auditEnteredAt(grant, 'fulfilled');
    case 'withdrawn': return auditEnteredAt(grant, 'withdrawn') ?? grant.reviewed_at ?? null;
    default: return null;
  }
}

/**
 * Calendar days between the stamp and `now`, on the viewer's clock (the one
 * the row's date prints in), or null when the stamp is unusable. #2837: this
 * counted 24-hour blocks, so a grant entered at 11 PM read "today" the next
 * morning while its date named yesterday.
 */
export function daysSince(iso: string | null | undefined, now: Date = new Date()): number | null {
  if (!iso) return null;
  return viewerCalendarDaysSince(iso, now);
}

const dayCount = (days: number) => (days === 1 ? '1 day' : `${days} days`);

/**
 * How long the request has been where it is, in words that name the stage.
 *
 *   "Draft since Sep 2, 2026"          a draft names the day it was filed
 *   "Pending since today"              submitted or under review, entered today
 *   "Pending for 3 days"               counted while recent
 *   "Pending since Aug 12, 2026"       past 28 days the date replaces the count
 *   "Approved Sep 12, 2026"            a settled row names its decision date
 *   "Time in this status unknown"      no usable stamp — never a number
 *
 * 2026-10-25: submitted read "Applied 3 days ago", the family's act. Both open
 * stages now read Pending (DECISIONS 2026-10-25), and a request staff filed
 * by phone was never the family's act of applying.
 */
export function grantTimeInStatusLabel(grant: TimedGrant, now: Date = new Date()): string {
  const since = grantStageEnteredAt(grant);
  const days = daysSince(since, now);
  const date = grantDate(since);
  const label = GRANT_STATUS_LABEL[grant.status] ?? grant.status;
  if (days === null || !date) return 'Time in this status unknown';
  if (GRANT_SETTLED_STATUSES.has(grant.status)) return `${label} ${date}`;
  if (grant.status === 'draft') return days === 0 ? 'Draft since today' : `Draft since ${date}`;
  if (days === 0) return `${label} since today`;
  if (days > RELATIVE_DAYS_LIMIT) return `${label} since ${date}`;
  return `${label} for ${dayCount(days)}`;
}

/**
 * The exact date behind a relative phrase, for the row's `title`. Undefined
 * when the phrase already names the date, or no date is known.
 */
export function grantTimeInStatusTitle(grant: TimedGrant, now: Date = new Date()): string | undefined {
  const since = grantStageEnteredAt(grant);
  const days = daysSince(since, now);
  const date = grantDate(since);
  if (days === null || !date) return undefined;
  if (GRANT_SETTLED_STATUSES.has(grant.status) || grant.status === 'draft') return undefined;
  if (days > RELATIVE_DAYS_LIMIT) return undefined;
  return `In this status since ${date}`;
}

/**
 * A grant date as the viewer's day: "Sep 12, 2026". It printed the UTC day
 * (`shortDate`), so an approval at 8 pm in Las Vegas read as the next day
 * (2026-10-11); then the Pacific day, which an Eastern reader saw a day early
 * after 9 pm. #2756 (#2704 / #2741): staff read event days on their own clock.
 */
function grantDate(iso: string | null | undefined): string | null {
  return trfEventDate(iso);
}

/** "Sep 12", the viewer's day — the month and day only, for a narrow column whose year is on hover. */
function monthDay(iso: string | null): string | null {
  return trfMonthDay(iso);
}

/**
 * The "In status" cell (grant lists redesign 2026-09-19, R5) — modelled on
 * Applications' `timeInStatusCell`. The status word prints once, in the chip,
 * so this cell carries only the clock:
 *
 *   open status     `4 days` / `1 day` / `today`; past 28 days `since Aug 12`
 *   settled status  the decision date, `Sep 12`
 *   unknown         `—`
 *
 * `title` is always the full phrase (`Under review for 4 days · since Sep 15,
 * 2026`), so nothing the row used to say is lost — it moved to hover.
 */
export function grantTimeInStatusCell(grant: TimedGrant, now: Date = new Date()): { text: string; title: string } {
  const phrase = grantTimeInStatusLabel(grant, now);
  const since = grantStageEnteredAt(grant);
  const days = daysSince(since, now);
  const full = grantDate(since);
  if (days === null || !full) return { text: '—', title: phrase };
  if (GRANT_SETTLED_STATUSES.has(grant.status)) return { text: monthDay(since) ?? full, title: phrase };
  if (days === 0) return { text: 'today', title: phrase };
  if (days > RELATIVE_DAYS_LIMIT) return { text: `since ${monthDay(since) ?? full}`, title: phrase };
  const title = grant.status === 'draft' ? phrase : `${phrase} · since ${full}`;
  return { text: dayCount(days), title };
}

/** The verb the audit trail's action reads as, in the reader's words. */
const AUDIT_VERB: Record<string, string> = {
  create: 'Filed',
  submitted: 'Applied',
  under_review: 'Review started',
  approved: 'Approved',
  denied: 'Denied',
  fulfilled: 'Closed',
  withdrawn: 'Withdrawn',
  payment: 'Payment recorded',
  paused: 'Payments paused',
  resumed: 'Payments resumed',
  // #2751: the renewal this grant came from was withdrawn / reopened.
  renewal_withdrawn: 'Renewal withdrawn',
  renewal_reopened: 'Renewal reopened',
};

/**
 * One provenance line from the audit trail — the newest entry, as verb + when
 * ("Approved on Sep 12, 2026"). The trail records the actor as an id only, and
 * an id is not a name (§1.2), so no actor prints until the read resolves one.
 * Null when the trail is empty or the newest entry has no usable date.
 */
export function grantProvenanceLine(grant: Pick<GrantRequest, 'audit_trail'>): string | null {
  const trail = (grant.audit_trail ?? []).filter((e) => e.at);
  if (trail.length === 0) return null;
  const last = trail[trail.length - 1];
  const date = grantDate(last.at);
  if (!date) return null;
  const verb = AUDIT_VERB[last.action] ?? 'Changed';
  return `${verb} on ${date}`;
}
